import { aiProxyClient } from "@/services/aiProxyClient";
import type {
  ClickUpChecklistItem,
  FirefliesTranscriptDetail,
  FormattedMeetingNotes,
} from "@/types/fireflies";

/** Cheap, capable model for structured meeting formatting */
export const FIREFLIES_FORMAT_MODEL = "google/gemini-2.5-flash";

const MAX_SENTENCES = 400;
const MAX_CHECKLIST_ITEMS = 5;

type AiClickUpPayload = {
  primaryPersonOrCompany?: string;
  mainTopic?: string;
  meetingDateTime?: string;
  meetingTitle?: string;
  participants?: string;
  meetingDateDepartment?: string;
  bluf?: string[];
  performanceForJamesBeth?: string;
  participantResponseCautions?: string;
  checklistItems?: Array<{ action?: string; dueDate?: string | null }>;
};

export type FormatMeetingTranscriptResult = {
  notes: FormattedMeetingNotes;
  model: string;
  usage?: { prompt_tokens?: number; completion_tokens?: number };
};

function formatTimestamp(ms: number | null | undefined): string {
  if (ms == null || Number.isNaN(ms)) return "";
  const totalSec = Math.floor(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}:${sec.toString().padStart(2, "0")}`;
}

function formatMeetingDateForTitle(dateMs: number | null | undefined): string {
  if (!dateMs) return "Unknown date";
  const d = new Date(dateMs);
  if (Number.isNaN(d.getTime())) return "Unknown date";
  return d.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function resolveRecordingLink(detail: FirefliesTranscriptDetail): string {
  return (
    detail.transcript_url?.trim() ||
    detail.meeting_link?.trim() ||
    detail.video_url?.trim() ||
    detail.audio_url?.trim() ||
    "Not available"
  );
}

function buildCompactPayload(detail: FirefliesTranscriptDetail): Record<string, unknown> {
  const sentences = (detail.sentences ?? []).slice(0, MAX_SENTENCES);
  const transcriptLines = sentences.map((s) => {
    const speaker = s.speaker_name || "Unknown";
    const time = formatTimestamp(s.start_time);
    const text = s.text?.trim() || "";
    return time ? `[${time}] ${speaker}: ${text}` : `${speaker}: ${text}`;
  });

  const attendees = (detail.meeting_attendees ?? [])
    .map((a) => a.displayName || a.name || a.email)
    .filter(Boolean);

  return {
    id: detail.id,
    title: detail.title,
    date: detail.date,
    dateFormatted: formatMeetingDateForTitle(detail.date),
    duration: detail.duration,
    host_email: detail.host_email,
    organizer_email: detail.organizer_email,
    attendees: attendees.length ? attendees : detail.participants ?? [],
    recordingLink: resolveRecordingLink(detail),
    fireflies_summary: detail.summary ?? {},
    transcript_excerpt: transcriptLines.join("\n"),
    truncated: (detail.sentences?.length ?? 0) > MAX_SENTENCES,
  };
}

function parseAiJson(content: string): AiClickUpPayload {
  const trimmed = content.trim();
  const jsonMatch = trimmed.match(/\{[\s\S]*\}/);
  const jsonStr = jsonMatch ? jsonMatch[0] : trimmed;
  return JSON.parse(jsonStr) as AiClickUpPayload;
}

function normalizeChecklistItems(raw: AiClickUpPayload["checklistItems"]): ClickUpChecklistItem[] {
  if (!Array.isArray(raw)) return [];

  const items: ClickUpChecklistItem[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const action = typeof item.action === "string" ? item.action.trim() : "";
    if (!action) continue;
    const dueDate =
      typeof item.dueDate === "string" && item.dueDate.trim()
        ? item.dueDate.trim()
        : null;
    items.push({ action, dueDate });
    if (items.length >= MAX_CHECKLIST_ITEMS) break;
  }
  return items;
}

function normalizeBluf(raw: unknown): [string, string] {
  const items = Array.isArray(raw)
    ? raw.filter((v): v is string => typeof v === "string" && v.trim().length > 0).map((s) => s.trim())
    : [];

  const first = items[0] || "No key outcomes identified.";
  const second = items[1] || "See meeting summary for additional context.";
  return [first, second];
}

function buildParentTaskTitle(
  primary: string,
  topic: string,
  dateTime: string
): string {
  return `${primary} - ${topic} - ${dateTime}`;
}

function buildParentTaskDescription(args: {
  meetingTitle: string;
  participants: string;
  meetingDateDepartment: string;
  bluf: [string, string];
  performanceForJamesBeth: string;
  participantResponseCautions: string;
  recording: string;
}): string {
  const lines = [
    `- **Meeting title:** ${args.meetingTitle}`,
    `- **Participants:** ${args.participants}`,
    `- **Meeting date & Department/category:** ${args.meetingDateDepartment}`,
    `- **BLUF:**`,
    `  - ${args.bluf[0]}`,
    `  - ${args.bluf[1]}`,
    `- **Action tasks (checklist):** This checklist contains up to 5 hard action items.`,
    `- **Performance for James/Beth:** ${args.performanceForJamesBeth}`,
    `- **Participant response / cautions:** ${args.participantResponseCautions}`,
    `- **Recording:** ${args.recording}`,
  ];
  return lines.join("\n");
}

function formatChecklistLine(item: ClickUpChecklistItem): string {
  const action = item.action.endsWith(".") ? item.action : `${item.action}.`;
  if (item.dueDate?.trim()) {
    return `${action} - ${item.dueDate.trim()}`;
  }
  return action;
}

function buildMarkdownBody(
  notes: Omit<FormattedMeetingNotes, "markdownBody">
): string {
  const lines: string[] = [
    `# ${notes.parentTaskTitle}`,
    "",
    "## Parent task description",
    "",
    notes.parentTaskDescription,
    "",
  ];

  if (notes.checklistItems.length) {
    lines.push("## Checklist", "");
    notes.checklistItems.forEach((item) => {
      lines.push(`- [ ] ${formatChecklistLine(item)}`);
    });
    lines.push("");
  }

  return lines.join("\n").trim();
}

export async function formatMeetingTranscript(
  detail: FirefliesTranscriptDetail,
  options?: { model?: string; signal?: AbortSignal }
): Promise<FormatMeetingTranscriptResult> {
  const model = options?.model ?? FIREFLIES_FORMAT_MODEL;
  const payload = buildCompactPayload(detail);
  const recordingLink = resolveRecordingLink(detail);
  const defaultDateTime = formatMeetingDateForTitle(detail.date);

  const systemPrompt = `You are a meeting-notes formatter for ClickUp parent tasks. Extract structured data from Fireflies meeting data.

Return JSON only (no markdown fences) with this exact shape:
{
  "primaryPersonOrCompany": "primary person name or company on the call",
  "mainTopic": "short main topic (3-8 words)",
  "meetingDateTime": "meeting date and time for task title, e.g. Wed, Jun 18, 2026, 6:50 AM",
  "meetingTitle": "meeting name or main topic",
  "participants": "comma-separated names or roles",
  "meetingDateDepartment": "weekday, month day, time if known; department or category if inferable",
  "bluf": ["exactly two one-sentence bullets summarizing most important outcomes or decisions"],
  "performanceForJamesBeth": "one specific presentation/call improvement for the primary driver",
  "participantResponseCautions": "one bullet combining participant response plus risks/watch-outs",
  "checklistItems": [
    { "action": "hard action item sentence", "dueDate": "Mon Jun 8, 3:00 PM or null" }
  ]
}

Rules:
- checklistItems: maximum 5 hard action items only. No subtasks. Flatten legacy subtasks into checklist items.
- dueDate: only include when explicitly stated in source text. Use format like "Mon Jun 8, 3:00 PM". Never invent dates. Use null when unknown.
- bluf: exactly 2 strings, each one sentence max.
- performanceForJamesBeth: exactly 1 specific improvement.
- participantResponseCautions: exactly 1 combined bullet.
- Do not repeat checklist items in other fields.
- Use recordingLink from input only for context; do not put it in checklistItems.`;

  const response = await aiProxyClient.chatCompletions(
    {
      model,
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: JSON.stringify(payload) },
      ],
      temperature: 0.2,
      max_tokens: 4096,
    },
    { signal: options?.signal, app: "fireflies_format" }
  );

  const json = await response.json();
  const content = json?.choices?.[0]?.message?.content;
  if (typeof content !== "string" || !content.trim()) {
    throw new Error("AI did not return formatted meeting notes");
  }

  let parsed: AiClickUpPayload;
  try {
    parsed = parseAiJson(content);
  } catch {
    throw new Error("Failed to parse AI meeting notes response");
  }

  const primaryPersonOrCompany =
    parsed.primaryPersonOrCompany?.trim() ||
    detail.host_email?.split("@")[0] ||
    "Unknown";
  const mainTopic =
    parsed.mainTopic?.trim() || detail.title?.trim() || "Meeting";
  const meetingDateTime = parsed.meetingDateTime?.trim() || defaultDateTime;
  const meetingTitle =
    parsed.meetingTitle?.trim() || detail.title?.trim() || mainTopic;

  const attendees = (detail.meeting_attendees ?? [])
    .map((a) => a.displayName || a.name || a.email)
    .filter(Boolean);
  const participants =
    parsed.participants?.trim() ||
    (attendees.length ? attendees.join(", ") : "Not specified");

  const meetingDateDepartment =
    parsed.meetingDateDepartment?.trim() || defaultDateTime;

  const bluf = normalizeBluf(parsed.bluf);
  const checklistItems = normalizeChecklistItems(parsed.checklistItems);

  const performanceForJamesBeth =
    parsed.performanceForJamesBeth?.trim() ||
    "Review pacing and clarity on the next similar call.";
  const participantResponseCautions =
    parsed.participantResponseCautions?.trim() ||
    "Monitor follow-through and confirm next steps in writing.";

  const parentTaskTitle = buildParentTaskTitle(
    primaryPersonOrCompany,
    mainTopic,
    meetingDateTime
  );

  const parentTaskDescription = buildParentTaskDescription({
    meetingTitle,
    participants,
    meetingDateDepartment,
    bluf,
    performanceForJamesBeth,
    participantResponseCautions,
    recording: recordingLink,
  });

  const notesBase: Omit<FormattedMeetingNotes, "markdownBody"> = {
    title: parentTaskTitle,
    parentTaskTitle,
    parentTaskDescription,
    checklistItems,
    date: meetingDateTime,
    durationMinutes: detail.duration ? Math.round(detail.duration) : null,
    attendees: attendees.length
      ? attendees
      : (detail.participants ?? []).filter(Boolean),
    summary: detail.summary?.short_summary || detail.summary?.gist || "",
    decisions: [],
    actionItems: checklistItems.map((item) => ({
      task: item.action,
      due: item.dueDate ?? null,
    })),
    topics: [],
    followUps: [],
    structured: {
      firefliesTranscriptId: detail.id,
      source: "fireflies",
      clickUp: {
        parentTaskTitle,
        parentTaskDescription,
        checklistItems,
      },
      summary: detail.summary?.short_summary || detail.summary?.gist || "",
      decisions: [],
      actionItems: checklistItems.map((item) => ({
        task: item.action,
        due: item.dueDate ?? null,
      })),
      topics: [],
      followUps: [],
      attendees: attendees.length
        ? attendees
        : (detail.participants ?? []).filter(Boolean),
    },
  };

  const notes: FormattedMeetingNotes = {
    ...notesBase,
    markdownBody: buildMarkdownBody(notesBase),
  };

  return {
    notes,
    model,
    usage: json?.usage,
  };
}
